<template>
  <Tooltip :delay-duration="0">
    <TooltipTrigger as-child>
      <button type="button" class="inline-flex items-center justify-center">
        <Info :class="cn('h-5 w-5 text-gray-400 hover:text-blue-400 cursor-help transition-colors', iconClass)" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="left" class="max-w-xs" :side-offset="8">
      <div v-if="loading" class="text-xs text-gray-400">
        {{ $t('gpu.loadingInfo') }}
      </div>
      <div v-else-if="error" class="text-xs text-red-400">
        {{ $t('gpu.errorLoadingInfo') }}
      </div>
      <div v-else-if="gpuInfo" class="text-xs space-y-2">
        <div class="border-b border-gray-700 pb-2 mb-2">
          <p class="font-semibold text-blue-400">{{ $t('gpu.hardwareInfo') }}</p>
        </div>
        <div>
          <span class="text-gray-400">{{ $t('gpu.gpuType') }}:</span>
          <span class="ml-1">{{ gpuInfo.gpu_type }}</span>
        </div>
        <div v-if="gpuInfo.cpu">
          <span class="text-gray-400">{{ $t('gpu.cpu') }}:</span>
          <span class="ml-1">{{ gpuInfo.cpu }}</span>
        </div>
        <div v-if="gpuInfo.ram">
          <span class="text-gray-400">{{ $t('gpu.ram') }}:</span>
          <span class="ml-1">{{ formatRam(gpuInfo.ram) }}</span>
        </div>
        <div v-if="gpuInfo.disk">
          <span class="text-gray-400">{{ $t('gpu.disk') }}:</span>
          <span class="ml-1">{{ gpuInfo.disk }} GB</span>
        </div>
        <div v-if="gpuInfo.vcpus">
          <span class="text-gray-400">{{ $t('gpu.vcpus') }}:</span>
          <span class="ml-1">{{ gpuInfo.vcpus }}</span>
        </div>
        <div v-if="gpuInfo.busway">
          <span class="text-gray-400">{{ $t('gpu.busway') }}:</span>
          <span class="ml-1">{{ gpuInfo.busway }}</span>
        </div>
        <div v-if="gpuInfo.switch">
          <span class="text-gray-400">{{ $t('gpu.switch') }}:</span>
          <span class="ml-1">{{ gpuInfo.switch }}</span>
        </div>
      </div>
    </TooltipContent>
  </Tooltip>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Info } from 'lucide-vue-next'
import { cn } from '@/lib/utils'

interface GpuInfo {
  gpu_type: string
  cpu?: string
  ram?: number
  disk?: number
  vcpus?: number
  busway?: string
  switch?: string
}

interface Props {
  name: string
  iconClass?: string
}

const props = defineProps<Props>()

const gpuInfo = ref<GpuInfo | null>(null)
const loading = ref(false)
const error = ref(false)

// 格式化 RAM 顯示 (MB 轉換為 GB 如果大於 1024)
const formatRam = (ram: number) => {
  if (ram >= 1024) {
    return `${(ram / 1024).toFixed(0)} GB`
  }
  return `${ram} MB`
}

// 取得 GPU 硬體資訊
const fetchGpuInfo = async () => {
  loading.value = true
  error.value = false

  try {
    // 使用 proxy API 呼叫 batch endpoint
    const response = await $fetch('/api/public/proxy/api/v3/gsn/flavors/batch', {
      method: 'POST',
      body: {
        names: [props.name]
      }
    }) as any

    // 從 batch response 取得第一筆資料
    if (response && response.result && response.content && response.content.length > 0) {
      const flavorData = response.content[0]
      gpuInfo.value = {
        gpu_type: flavorData.gpu_type || '',
        cpu: flavorData.cpu || '',
        ram: flavorData.ram || 0,
        disk: flavorData.disk || 0,
        vcpus: flavorData.vcpus || 0,
        busway: flavorData.busway || '',
        switch: flavorData.switch || ''
      }
    } else {
      error.value = true
    }
  } catch (err) {
    console.error('Failed to fetch GPU info:', err)
    error.value = true
  } finally {
    loading.value = false
  }
}

// 當 name 改變時重新取得資料
watch(() => props.name, () => {
  if (props.name) {
    fetchGpuInfo()
  }
}, { immediate: true })
</script>
